// API Configuration - Uses environment variable for backend URL
const getApiBaseUrl = () => {
  // Check if we have an environment variable set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on current hostname
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:5000';
  }
  
  // Production - use Render backend
  return 'https://sahayak-xbt3.onrender.com';
};

const API_BASE_URL = getApiBaseUrl();

console.log('🔗 API Base URL:', API_BASE_URL);

// Create axios instance with default configuration
const apiClient = {
  // Helper function to make API requests
  request: async (method, endpoint, data = null, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('token');
    
    const config = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`API ${method} ${endpoint} error:`, error);
      throw error;
    }
  }
};

// Authentication API
export const authAPI = {
  // Register a new user (supports both email and phone)
  register: async (userData) => {
    return apiClient.request('POST', '/api/auth/register', userData);
  },

  // Login user (supports both email and phone via identifier)
  login: async (credentials) => {
    return apiClient.request('POST', '/api/auth/login', credentials);
  },

  // Get current user profile
  getCurrentUser: async () => {
    return apiClient.request('GET', '/api/auth/me');
  },

  // Get dashboard statistics
  getDashboardStats: async () => {
    return apiClient.request('GET', '/api/auth/dashboard-stats');
  }
};

// Helper API
export const helperAPI = {
  // Get all helpers with filtering
  getHelpers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = `/api/helpers${queryString ? `?${queryString}` : ''}`;
    return apiClient.request('GET', endpoint);
  },

  // Get specific helper by ID
  getHelper: async (id) => {
    return apiClient.request('GET', `/api/helpers/${id}`);
  },

  // Update helper profile
  updateHelperProfile: async (profileData) => {
    return apiClient.request('PUT', '/api/helpers/profile', profileData);
  }
};

// Booking API
export const bookingAPI = {
  // Calculate pricing for a booking
  calculatePricing: async (pricingData) => {
    return apiClient.request('POST', '/api/bookings/calculate-pricing', pricingData);
  },

  // Create a new booking
  createBooking: async (bookingData) => {
    return apiClient.request('POST', '/api/bookings', bookingData);
  },

  // Get user's bookings (role-based)
  getMyBookings: async () => {
    return apiClient.request('GET', '/api/bookings/my-bookings');
  },

  // Update booking status
  updateBookingStatus: async (bookingId, status) => {
    return apiClient.request('PATCH', `/api/bookings/${bookingId}/status`, { status });
  },

  // Get specific booking details
  getBooking: async (bookingId) => {
    return apiClient.request('GET', `/api/bookings/${bookingId}`);
  }
};

// Payment API
export const paymentAPI = {
  // Create Razorpay order
  createOrder: async (bookingId) => {
    return apiClient.request('POST', '/api/payments/create-order', { bookingId });
  },

  // Verify Razorpay payment
  verifyPayment: async (paymentData) => {
    return apiClient.request('POST', '/api/payments/verify', paymentData);
  },

  // Handle payment failure
  handlePaymentFailure: async (bookingId, error) => {
    return apiClient.request('POST', '/api/payments/payment-failed', { bookingId, error });
  },

  // Get payment status
  getPaymentStatus: async (bookingId) => {
    return apiClient.request('GET', `/api/payments/status/${bookingId}`);
  }
};

// Review API
export const reviewAPI = {
  // Create a new review
  createReview: async (reviewData) => {
    return apiClient.request('POST', '/api/reviews', reviewData);
  },

  // Get reviews for a specific helper
  getHelperReviews: async (helperId) => {
    return apiClient.request('GET', `/api/reviews/helper/${helperId}`);
  }
};

// User API (for profile management)
export const userAPI = {
  // Update user profile
  updateProfile: async (profileData) => {
    return apiClient.request('PUT', '/api/users/profile', profileData);
  },
  
  // Get all saved addresses
  getAddresses: async () => {
    return apiClient.request('GET', '/api/users/addresses');
  },
  
  // Add a new address
  addAddress: async (addressData) => {
    return apiClient.request('POST', '/api/users/addresses', addressData);
  },
  
  // Update an existing address
  updateAddress: async (addressId, addressData) => {
    return apiClient.request('PUT', `/api/users/addresses/${addressId}`, addressData);
  },
  
  // Delete an address
  deleteAddress: async (addressId) => {
    return apiClient.request('DELETE', `/api/users/addresses/${addressId}`);
  },
  
  // Set an address as default
  setDefaultAddress: async (addressId) => {
    return apiClient.request('PATCH', `/api/users/addresses/${addressId}/set-default`);
  }
};

// Health check API
export const healthAPI = {
  // Check API health
  checkHealth: async () => {
    return apiClient.request('GET', '/api/health');
  }
};

// Export the base URL for use in other components
export { API_BASE_URL }; 